import React, { useEffect, useMemo, useState } from "react";

import { graphql, useStaticQuery } from "gatsby";
import { useIsSSR } from "react-aria";

import { gql } from "@apollo/client";
import styled from "@emotion/styled";

import { useGraphqlLaravelLighthouse } from "../../../../providers/GraphqlLaravelLighthouse";
import ArticleCard from "../../ArticleCard";
import Carousel from "../../Carousel";
import { contentContainer } from "../../ContentContainer";
import LinkComponent from "../../LinkComponent";
import NewHeadline from "../../NewHeadline";

const Container = styled.div`
    ${contentContainer}
    ${({ theme }) => theme.breakpoints.upTo.small.css`
        padding: 0 30px;
    `}

    ${({ theme }) => theme.breakpoints.upTo.extraSmall.css`
        padding: 0 20px;
    `}
`;

const articleWidth = 260;

const Item = styled(Carousel.Item)`
    width: ${articleWidth}px;
`;

const LinkRow = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

export const Articles = ({ articles, name, linkText, allEntriesLinkText }) => (
    <Container>
        <NewHeadline color="blue">{name}</NewHeadline>
        <Carousel>
            {articles.map((article, i) => (
                <Item key={i}>
                    <ArticleCard
                        article={article}
                        linkText={linkText}
                        width={articleWidth}
                    />
                </Item>
            ))}
        </Carousel>
        <LinkRow>
            <LinkComponent
                link={{ href: "/news", text: allEntriesLinkText }}
                icon="arrow-right"
                color="blue"
            />
        </LinkRow>
    </Container>
);

const isOfType = (types) => (article) =>
    types.size === 0 ||
    (types.has("gemeinnützigkeit") && article.section === "Gemeinnützigkeit") ||
    (types.has("tankstelle") &&
        (article.websites ?? []).some(
            ({ websiteName }) => websiteName === "hoyer-tankstellen.de"
        ));

export const dynamicQuery = gql`
    query {
        article: newsArticles(
            orderBy: { order: DESC, column: DATE_PUBLISHED }
            isPublished: true
            source: "hoyer.de"
        ) {
            abstract
            articleBody
            createdAt
            updatedAt
            datePublished
            image
            keywords
            section
            slug
            title
            titleAlignment
            video
        }
    }
`;

export const filterArticles = (articles, types) =>
    articles.filter(
        isOfType(
            new Set(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                types.map(
                    (t) =>
                        ({
                            1: "gemeinnützigkeit",
                            2: "tankstelle",
                        }[t])
                )
            )
        )
    );

export const ArticlesContainer = ({ types = [], ...props }) => {
    const [freshNews, setFreshNews] = useState(undefined);
    const client = useGraphqlLaravelLighthouse();

    const isSSR = useIsSSR();

    const { laravelLighthouse } = useStaticQuery(graphql`
        query {
            laravelLighthouse {
                articles: newsArticles(
                    orderBy: { order: DESC, column: DATE_PUBLISHED }
                    isPublished: true
                    source: "hoyer.de"
                ) {
                    title
                    image
                    slug
                    datePublished
                    section
                    websites: newsSources {
                        websiteName: name
                    }
                }
            }
        }
    `);

    const articles = useMemo(
        () => filterArticles(laravelLighthouse.articles, types).slice(0, 4),
        [laravelLighthouse, types]
    );

    const loadNews = async () => {
        /**
         * We will compare the news from the static content to the news in our database.
         */
        if (!isSSR && client) {
            const data = await client.query({ query: dynamicQuery });
            const dynamicArticles = filterArticles(
                data?.data?.article,
                types
            ).slice(0, 4);
            if (
                dynamicArticles.length > 0 &&
                articles.length > 0 &&
                articles[0].slug !== dynamicArticles[0].slug
            ) {
                setFreshNews(dynamicArticles);
            }
        }
    };

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        loadNews();
    }, [isSSR]);

    return <Articles {...{ articles: freshNews ?? articles, ...props }} />;
};

export default ArticlesContainer;
